import * as Sentry from '@sentry/react';
import { createRouter, ErrorComponent, RouterProvider } from '@tanstack/react-router';
import { authnClient } from '@frontend/auth';
import envs from '@frontend/env';
import { getNavigationStartTime, sentry, useSentrySpanMarker } from '@frontend/tracking';
import { ThemeProvider, AlertsProvider } from '@frontend/design-system';
import { ErrorBoundary } from './components/error-boundary';
import { routeTree } from './routeTree.gen';
import { preloadAssets } from './services/preload-nwx';

const router = createRouter({
  routeTree,
  defaultPendingComponent: () => <div>Loading</div>,
  defaultErrorComponent: ({ error }) => <ErrorComponent error={error} />,
  context: { auth: authnClient },
});

type SentryEnvs = 'production' | 'debug';
const sentryEnvironment: SentryEnvs = location.hostname.includes('f.weavedev.net') ? 'debug' : 'production';
const DEFAULT_SAMPLE_RATE = sentryEnvironment === 'production' ? 0.25 : 0;

sentry.init({
  dsn: envs.SENTRY_DSN,
  // "HTTP error 401" - see new-auth/fetch for where this error message comes from
  // "TypeError: Failed to fetch" -- the browser throws this when fetch fails to even
  //    send a request for example, if the user is offline or the URL is invalid. Due
  //    to how common internet connectivity issues are, we ignore this error to avoid
  //    overwhelming Sentry with noise.
  // 410 Gone - occurs when hydra sees a user try to use a login request that has been already handled
  // 429 Account currently locked - the user has too many failed login attempts
  // "login request was cancelled" - occurs when the user logging in differs from the user in the active
  //    Hydra session. This is an expected condition handled gracefully in the UI (redirects to re-initiate
  //    the login flow). It can surface as an unhandled promise rejection via async event handlers.
  ignoreErrors: [
    /(HTTP error|Request failed with status) (401|403|410|429)/i,
    'TypeError: Failed to fetch',
    /410 Gone/i,
    'login request was cancelled',
  ],
  denyUrls: ['chrome-extension://'],
  integrations: [
    Sentry.browserTracingIntegration({ instrumentNavigation: true }),
    Sentry.tanstackRouterBrowserTracingIntegration(router) as any, // ignoring type error for now
  ],
  tracesSampleRate: sentryEnvironment === 'production' ? 0.1 : 0,
  environment: sentryEnvironment,
  beforeSend(event: Sentry.ErrorEvent, _hint: Sentry.EventHint) {
    // the event can override the default sample rate in production, otherwise it falls back to the default
    const eventSampleRate =
      sentryEnvironment === 'production' && typeof event?.contexts?.meta?.sampleRate === 'number'
        ? event.contexts.meta.sampleRate
        : undefined;
    const sampleRate = eventSampleRate ?? DEFAULT_SAMPLE_RATE;
    return Math.random() < sampleRate ? event : null;
  },
});

// Preload NWX assets after a short delay so that preloading has less perf impact on the initial page load.
setTimeout(async () => {
  try {
    await preloadAssets();
  } catch (error) {
    // This is not a critical feature, so we can safely ignore errors here.
    // We also don't want to log this to Sentry as it would create too much noise.
    console.warn('Error preloading assets:', error);
  }
}, 500);

export default function Root() {
  console.log('version: ', envs.VERSION);
  /* on first load do these checks:
      handle routing, is it a call back, magic link, password reset, etc.
      check if user is already signed in, if so, redirect to the requested app
      initialize state key so we can have multiple tabs open and logging into different apps
  */

  // Set the startTime to when the browser starts handling a request which gives us the complete time till the user can do something in gatekeeper
  useSentrySpanMarker('Gatekeeper app mounted', 'ui.gatekeeper.mounted', getNavigationStartTime());

  return (
    <ThemeProvider includeEmotionTheme skipAnimation={!!window?.Cypress}>
      <Sentry.ErrorBoundary fallback={(props) => <ErrorBoundary {...props} />}>
        <AlertsProvider>
          <RouterProvider
            router={router}
            context={{
              auth: authnClient,
            }}
          />
        </AlertsProvider>
      </Sentry.ErrorBoundary>
    </ThemeProvider>
  );
}

declare global {
  interface Window {
    Cypress?: any;
  }
}
